import type { Container, Sprite } from 'pixi.js';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, TAU, paintSheet } from '../paint';
import { type Look, fade } from '../player-look';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import { CHICK_FEET, FEET, HOOD, POUSSIN_PIECES, UNIT, WINGS } from './poussin-paint';

type Parts = Readonly<Record<keyof typeof POUSSIN_PIECES, Shape>>;

const EXTENT = 32;
const MIDDLE = 2;
const BOB_TICKS = 2;
const BEAT_SWELL = 0.04;
const STEP = { rest: 0.25, walking: 0.6 } as const;
const CALM = { swell: 0.3, wings: 0.3, step: 0.5 } as const;
const PORTRAIT_CHICKS = { count: 3, along: 26, across: 25, perBeat: 0.5, hop: 0.08, size: 1.4 };

export interface ChickSprites {
  readonly body: Sprite;
  readonly left: Sprite;
  readonly right: Sprite;
}

export function chickSprites(
  parent: Container,
  shapes: Pick<Parts, 'chick' | 'chickFoot'>,
): ChickSprites {
  return {
    left: add(parent, shapes.chickFoot),
    right: add(parent, shapes.chickFoot),
    body: add(parent, shapes.chick),
  };
}

export function placeChick(
  sprites: ChickSprites,
  x: number,
  y: number,
  angle: number,
  size: number,
  step: number,
): void {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const put = (sprite: Sprite, along: number, across: number) => {
    sprite.visible = true;
    sprite.position.set(
      x + (along * cos - across * sin) * UNIT * size,
      y + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = angle;
    sprite.scale.set(size);
  };
  put(sprites.left, CHICK_FEET.along + step * CHICK_FEET.stride, -CHICK_FEET.across);
  put(sprites.right, CHICK_FEET.along - step * CHICK_FEET.stride, CHICK_FEET.across);
  put(sprites.body, 0, 0);
}

function createChickLook(bodies: Container, parts: Parts): Look {
  const leftFoot = add(bodies, parts.foot);
  const rightFoot = add(bodies, parts.foot);
  const leftWing = add(bodies, parts.wingLeft);
  const rightWing = add(bodies, parts.wingRight);
  const outline = add(bodies, parts.silhouette);
  const body = add(bodies, parts.body);
  const tuft = add(bodies, parts.tuft);
  const hood = add(bodies, parts.hood);
  const face = add(bodies, parts.face);
  const chicks = Array.from({ length: PORTRAIT_CHICKS.count }, () => chickSprites(bodies, parts));
  const sprites = [leftFoot, rightFoot, leftWing, rightWing, body, tuft, hood, face];
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
    sprite.scale.set(size);
  }

  return {
    hide() {
      hide(...sprites, outline);
      for (const chick of chicks) {
        hide(chick.body, chick.left, chick.right);
      }
    },
    place(input) {
      const { x, y, angle, scale, color, frame } = input;
      const { now, calm, tempo, camera } = frame;
      const beats = now / tempo.ticksPerBeat;
      const swell = fade(input.sinceBeat, BOB_TICKS) * BEAT_SWELL * (calm ? CALM.swell : 1);
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale * (1 + swell);
      facing = angle;
      for (const sprite of sprites) {
        sprite.visible = true;
      }

      const stride =
        Math.sin(Math.PI * beats) *
        (STEP.rest + STEP.walking * input.moving) *
        FEET.stride *
        (calm ? CALM.step : 1);
      put(leftFoot, FEET.along + stride, -FEET.across);
      put(rightFoot, FEET.along - stride, FEET.across);

      const flap = WINGS.rest + WINGS.beat * Math.sin(TAU * beats) * (calm ? CALM.wings : 1);
      put(leftWing, 0, -WINGS.across, -flap);
      put(rightWing, 0, WINGS.across, flap);
      setTint(leftWing, color);
      setTint(rightWing, color);

      put(body, 0, 0);
      setTint(body, color);
      placeOutline(outline, body, parts.silhouette.texture, REFERENCE_RADIUS, frame);
      put(tuft, HOOD.along + HOOD.tuft, 0);
      setTint(tuft, 0xffffff);
      put(hood, HOOD.along, 0);
      setTint(hood, color);
      put(face, HOOD.along, 0);
      setTint(face, 0xffffff);

      if (!frame.portrait) {
        for (const chick of chicks) {
          hide(chick.body, chick.left, chick.right);
        }
        return 0;
      }
      const unit = UNIT * scale;
      const half = Math.min(camera.screenWidth, camera.screenHeight) / (2 * camera.scale);
      const room = Math.max(0, half / unit - 7 * PORTRAIT_CHICKS.size);
      chicks.forEach((chick, index) => {
        const turn = (calm ? 0.3 : 1) * beats * PORTRAIT_CHICKS.perBeat;
        const around = turn + (index * TAU) / PORTRAIT_CHICKS.count;
        const hop = calm ? 0 : Math.abs(Math.sin(Math.PI * (beats + index / 3)));
        placeChick(
          chick,
          x + Math.cos(around) * Math.min(PORTRAIT_CHICKS.along, room) * unit,
          y + Math.sin(around) * Math.min(PORTRAIT_CHICKS.across, room) * unit,
          around + Math.PI / 2,
          scale * PORTRAIT_CHICKS.size * (1 + PORTRAIT_CHICKS.hop * hop),
          Math.sin(TAU * 2 * beats) * (calm ? CALM.step : 1),
        );
      });
      return 0;
    },
  };
}

export const POUSSIN_DRAWING: ClassDrawing = {
  classId: 'poussin',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(POUSSIN_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container) => createChickLook(bodies, shapes),
    };
  },
};
